import React, { useState, useEffect } from 'react';
import { Truck, Award, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';

export const PromotionalBanners: React.FC = () => {
  const [activeMobileIndex, setActiveMobileIndex] = useState(0);

  const banners = [
    {
      id: 1,
      badge: 'โปรโมชั่นพิเศษ',
      badgeIcon: <Truck className="w-3.5 h-3.5 text-amber-300 fill-amber-300/30" />,
      title: 'จัดส่งฟรีทั่วไทย 500.-',
      desc: 'ช้อปสินค้าครบ 500 บาท จัดส่งถึงหน้าบ้านทันที (ส่งด่วน 1-2 วัน)',
      icon: <Truck className="w-7 h-7 sm:w-8 sm:h-8 text-white" />,
      bgClass: 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-blue-600/20 border-blue-300/30',
      badgeClass: 'bg-white/20 text-white',
      iconBoxClass: 'bg-white/18 border-white/25 text-white',
    },
    {
      id: 3,
      badge: 'บริการสะดวกสบาย',
      badgeIcon: <Truck className="w-3.5 h-3.5 text-slate-700" />,
      title: 'เก็บเงินปลายทาง (COD)',
      desc: 'ปลอดภัย 100% สั่งสินค้าแล้วรอรับของและจ่ายเงินสดที่หน้าบ้านได้เลย',
      icon: <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 stroke-[1.8] text-slate-800" />,
      bgClass: 'bg-[linear-gradient(115deg,#9ca1a7_0%,#c8ccd1_18%,#eaedef_38%,#ffffff_52%,#dee1e5_68%,#b4b8be_85%,#8d9298_100%)] text-slate-800 border-white/70 hover:border-white shadow-md',
      badgeClass: 'bg-white/50 text-slate-800 border border-white/80 shadow-xs font-semibold',
      iconBoxClass: 'bg-white/45 text-slate-800 border border-white/80 shadow-sm backdrop-blur-xs',
    },
  ];

  // Auto-slide on mobile every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveMobileIndex((prev) => (prev + 1) % banners.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [banners.length]);

  return (
    <div className="w-full my-3 sm:my-4">
      {/* Mobile Slider View (Slide-per-block) */}
      <div className="block md:hidden">
        <div className="relative overflow-hidden rounded-[24px] bg-white/50 backdrop-blur-md border border-slate-200/80 shadow-sm p-2">
          <div 
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${activeMobileIndex * 100}%)` }}
          >
            {banners.map((b) => (
              <div key={b.id} className="w-full shrink-0 px-1">
                <div className={`relative rounded-[20px] ${b.bgClass} p-4 shadow-md flex items-center justify-between overflow-hidden border`}>
                  <div className="space-y-1.5 z-10 min-w-0 pr-2">
                    <div className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] font-semibold tracking-wide backdrop-blur-xs ${b.badgeClass}`}>
                      {b.badgeIcon}
                      <span>{b.badge}</span>
                    </div>
                    <h3 className={`text-[15px] font-extrabold tracking-tight leading-snug ${b.id === 1 ? 'text-white' : 'text-slate-900'}`}>
                      {b.title}
                    </h3>
                    <p className={`text-[11.5px] font-medium leading-relaxed ${b.id === 1 ? 'text-blue-100/90' : 'text-slate-700'}`}>
                      {b.desc}
                    </p>
                  </div>
                  <div className={`w-13 h-13 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${b.iconBoxClass}`}>
                    {b.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Slider Dots & Nav Controls */}
          <div className="flex items-center justify-between px-3 pt-2 pb-1">
            <button 
              onClick={() => setActiveMobileIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1))}
              className="w-7 h-7 rounded-full bg-white/80 border border-slate-200 text-blue-600 flex items-center justify-center shadow-2xs hover:bg-slate-50 active:scale-95 transition-all"
              aria-label="Previous Slide"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-1.5">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveMobileIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    activeMobileIndex === i ? 'w-6 bg-[#2A59FE]' : 'w-1.5 bg-slate-200 hover:bg-slate-300'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button 
              onClick={() => setActiveMobileIndex((prev) => (prev + 1) % banners.length)}
              className="w-7 h-7 rounded-full bg-white/80 border border-slate-200 text-blue-600 flex items-center justify-center shadow-2xs hover:bg-slate-50 active:scale-95 transition-all"
              aria-label="Next Slide"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Grid View (2 Columns) */}
      <div className="hidden md:grid grid-cols-2 gap-4 p-2 rounded-[26px] bg-white/40 backdrop-blur-md border border-slate-200/80 shadow-sm">
        {banners.map((b) => (
          <div key={b.id} className={`relative rounded-[22px] ${b.bgClass} p-4.5 shadow-md flex items-center justify-between overflow-hidden border transition-transform duration-200 hover:-translate-y-0.5`}>
            <div className="space-y-1.5 z-10 min-w-0 pr-2">
              <div className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] font-semibold tracking-wide backdrop-blur-xs ${b.badgeClass}`}>
                {b.badgeIcon}
                <span>{b.badge}</span>
              </div>
              <h3 className={`text-base font-extrabold tracking-tight leading-snug ${b.id === 1 ? 'text-white' : 'text-slate-900'}`}>
                {b.title}
              </h3>
              <p className={`text-xs font-medium leading-relaxed ${b.id === 1 ? 'text-blue-100/90' : 'text-slate-700'}`}>
                {b.desc}
              </p>
            </div>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${b.iconBoxClass}`}>
              {b.icon}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

